/**
 *
 *
 * @author Juan Rico
 *
 * @module Informes
 *
 * @description Este módulo define el la pagina de Informes o `Reports`, que permite visualizar y gestionar informes.
 * El componente se encarga de la obtención y eliminación de informes, así como de la
 * visualización de informes individuales con sus respectivos detalles.
 *
 */

/**
 * Importaciones necesarias para el la pagina de informes.
 */
import React, { useContext, useEffect, useState } from "react"; // Importa React y hooks.
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"; // Componente para usar íconos de FontAwesome.
import {
  faArrowLeft,
  faTrashAlt,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons"; // Íconos específicos para el componente.
import { iconInforme } from "../../helpers/ImagesCache"; //Iconos especificos cargados en cache por el desarrolador
import "./reports.css"; // importacion de la hoja de estilos para esta pagina
import Header from "../../components/Header/Header"; // Componente de la cabezera de la pagina.
import { GlobalContext, TimelineContext } from "../../helpers/Context"; // Contexto que proporciona información sobre la línea de tiempo.
import { BASEURLAPI } from "../../helpers/Constants"; // URL base para las solicitudes API.
import NSListItem from "../../components/NSButton/Types/ListItem"; // Componente para mostrar cada informe en la lista.
import GraphicalReport from "../../components/GraphicalReport/GraphicalReport";
import { Tooltip } from "react-tooltip";

import useTranslation from "../../hooks/useTranslation";
import { generatePdfFromElement } from "../../utils/pdfGenerator";
import DialogSureToDelete from "../../components/DialogSureToDelete";

/**
 * Componente principal que maneja la visualización de informes.
 *
 * @function Reports
 * @returns {JSX.Element} Un elemento que representa la interfaz de informes.
 */

const Reports = ({ preview }) => {
  const T = useTranslation();
  /**
   * Contexto de la linea de tiempo.
   * @type {Object}
   */
  const ctxTimeline = useContext(TimelineContext);

  /**
   * Contexto de la linea de tiempo.
   * @type {Object}
   */
  const globalCtx = useContext(GlobalContext);

  /**
   * Estado que almacena el ID del informe actual seleccionado.
   * @type {number}
   */
  const [currentReport, setCurrentReport] = useState(0);

  /**
   * Estado que almacena los detalles del informe seleccionado.
   * @type {Object}
   */
  const [selectedReport, setSelectedReport] = useState({});

  /**
   * Estado que almacena el ID del usuario actual.
   * @type {number}
   */
  const [userId, setUserId] = useState(globalCtx.userData?.id);

  /**
   * Estado que almacena la lista de informes obtenidos.
   * @type {Array<Object>}
   */
  const [reports, setReports] = useState([]);

  /**
   * Estado que indica si los informes se están cargando.
   * @type {boolean}
   */
  const [isLoading, setIsLoading] = useState(true);

  const [showDeleteReportDialog, setShowDeleteReportDialog] = useState(false);

  /**
   * Componente que renderiza la lista de informes.
   *
   * @function ListOfReports
   * @returns {JSX.Element} Un elemento que muestra la lista de informes.
   */
  const ListOfReports = () => {
    return (
      <div className="flex flex-col  w-full">
        {reports?.length > 0 ? (
          reports
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map((item, index) => {
              return (
                <NSListItem
                  key={"reports-" + item.id}
                  options={item}
                  onClick={(item) => viewReport(item)}
                />
              );
            })
        ) : (
          <span className="ml-10 text-xl text-[#f009] font-bold">
            {isLoading ? T("Cargando datos") : T("No hay Informes creados")}
          </span>
        )}
      </div>
    );
  };

  /**
   * Maneja la visualización de un informe específico.
   *
   * @function viewReport
   * @param {number} reportId - ID del informe a visualizar.
   */
  const viewReport = (report) => {
    try {
      console.log("1. Intentando ver informe:", report);
      setSelectedReport(report);
      setCurrentReport(report.id);
    } catch (error) {
      console.error("Error al ver informe:", error);
      console.error("Error stack:", error.stack);
    }
  };

  /**
   * Regresa al estado inicial (sin informe seleccionado).
   *
   * @function goToRoot
   */
  const goToRoot = () => {
    setCurrentReport(0);
  };

  const handleDownloadPdf = async () => {
    try {
      // Forzar re-renderizado del elemento antes de generar PDF
      const reportElement = document.getElementById("report-pdf");
      
      // Ocultar y mostrar el elemento para forzar re-renderizado
      reportElement.style.display = "none";
      await new Promise(resolve => setTimeout(resolve, 50));
      reportElement.style.display = "block";
      await new Promise(resolve => setTimeout(resolve, 100));
      
      await generatePdfFromElement(reportElement, selectedReport.name);
    } catch (error) {
      console.error("Error generando PDF:", error);
    }
  };

  /**
   * Obtiene todos los informes del usuario.
   *
   * @function getAllReports
   */
  const getAllReports = () => {
    try {
      console.log("1. Obteniendo informes de localStorage...");
      const reportsData = localStorage.getItem("reports");
      console.log("2. Datos raw de localStorage:", reportsData);

      const reports = JSON.parse(reportsData || "[]");
      console.log("3. Informes parseados:", reports);

      setReports(reports);
      setIsLoading(false);
    } catch (error) {
      console.error("Error obteniendo informes:", error);
      console.error("Error stack:", error.stack);
      setIsLoading(false);
    }
  };

  /**
   * Elimina un caso específico.
   *
   * @function deleteCase
   * @param {number} id - ID del caso a eliminar.
   */
  const deleteCase = (reportId) => {
    try {
      console.log("Eliminando informe con ID:", reportId, "Tipo:", typeof reportId);
      const reports = JSON.parse(localStorage.getItem("reports") || "[]");
      console.log("Informes antes de eliminar:", reports.length);
      console.log("IDs de informes existentes:", reports.map(r => ({ id: r.id, type: typeof r.id })));
      
      // Convertir reportId a número para asegurar comparación correcta
      const numericReportId = Number(reportId);
      console.log("ID convertido a número:", numericReportId);
      
      const updatedReports = reports.filter((report) => Number(report.id) !== numericReportId);
      console.log("Informes después de eliminar:", updatedReports.length);
      
      localStorage.setItem("reports", JSON.stringify(updatedReports));
      
      // Verificar que se eliminó correctamente
      const savedReports = JSON.parse(localStorage.getItem("reports"));
      console.log("Verificación de eliminación:", savedReports.length);
      
      // Actualizar el estado local
      setReports(updatedReports);
      setCurrentReport(0);
      setSelectedReport({});
      
      console.log("Informe eliminado exitosamente");
    } catch (error) {
      console.error("Error eliminando informe:", error);
      console.error("Error stack:", error.stack);
    }
  };

  const handleDeletePressed = () => {
    setShowDeleteReportDialog(true);
  };

  const getSelectedReport = () => {
    if (selectedReport && selectedReport.timeline) {
      const currentTempReport = {
        ...selectedReport,
        timeline:
          typeof selectedReport.timeline === "string"
            ? JSON.parse(selectedReport.timeline)
            : selectedReport.timeline,
      };
      return currentTempReport;
    }
    return {};
  };

  /**
   * Hook que se ejecuta al montar el componente y cada vez que userId es actualizado.
   */
  useEffect(() => {
    getAllReports();
  }, [userId]);

  /**
   * Hook que se ejecuta al montar el componente y cada vez que currentReport es actualizado.
   */
  useEffect(() => {
    getSelectedReport();
  }, [currentReport]);

  useEffect(() => {
    console.log("selectedReport", selectedReport);
  }, [selectedReport]);

  return (
    <div className="flex flex-col bg-white max-w-[1280px] w-[100vw]">
      <div className="flex flex-col  bg-[#fff] flex w-full  justify-start gap-0 ">
        <Header minimal simple />
        <div className="flex flex-row gap-4 font-[600] justify-between px-5 mt-5">
          <div className="flex flex-row items-center gap-5">
            {currentReport > 0 && (
              <FontAwesomeIcon
                className="cursor-pointer"
                icon={faArrowLeft}
                onClick={goToRoot}
              />
            )}
            <img src={iconInforme} alt={iconInforme} />
            <span>{T("Informes")}</span>
          </div>
          <div className="flex flex-row items-center">
            <div className="flex gap-4 cursor-pointer items-center justify-center h-full w-10 text-2xl text-[#2aac9c]">
              {currentReport > 0 && (
                <FontAwesomeIcon
                  className="hover:bg-red-100 cursor-pointer hover:text-red-500 flex text-black"
                  icon={faFilePdf}
                  onClick={handleDownloadPdf}
                />
              )}
            </div>
            <div className="flex gap-4 cursor-pointer items-center justify-center h-full w-10 text-2xl text-[#2aac9c]">
              {currentReport > 0 && (
                <FontAwesomeIcon
                  className="hover:bg-red-100 cursor-pointer hover:text-red-500 flex text-black"
                  icon={faTrashAlt}
                  onClick={() => setShowDeleteReportDialog(true)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className=" p-4  flex flex-col  bg-[#fff] flex w-full items-center justify-start gap-5 ">
        {currentReport == 0 && <ListOfReports />}

        {currentReport > 0 && (
          <GraphicalReport
            isNameEditable={false}
            selectedReport={getSelectedReport}
            historyReport={true}
            minimized={false}
          />
        )}

        <div
          id="report-pdf"
          className="hidden p-[10px]"
          style={{
            width: "auto",
            minWidth: "auto",
            maxWidth: "none",
            margin: "0 auto",
            backgroundColor: "#ffffff",
          }}
        >
          {currentReport > 0 && (
            <GraphicalReport
              isNameEditable={false}
              selectedReport={getSelectedReport}
              historyReport={true}
              minimized={false}
            />
          )}
        </div>
      </div>
      <DialogSureToDelete
        visible={showDeleteReportDialog}
        onClose={() => {
          console.log("Cancelando eliminación de informe");
          setShowDeleteReportDialog(false);
        }}
        onConfirm={() => {
          console.log("Confirmando eliminación de informe con ID:", selectedReport.id);
          deleteCase(selectedReport.id);
          setShowDeleteReportDialog(false);
        }}
      />
    </div>
  );
};
export default Reports;
